import { useState } from "react";
import TransferFundsModal from "@/pages/admin/contabilidad/components/TransferFundsModal";
import type { FundsSummary, FundSummary } from "@/utils/accounting";
import type { FundKey, FundMovement } from "@/types";
import { formatCOP, formatDate } from "@/utils/format";

const TONES: Record<FundKey, { bubble: string; bar: string; text: string }> = {
  caja: { bubble: "bg-primary-500/25 text-primary-100", bar: "bg-primary-500", text: "text-primary-200" },
  asesor: { bubble: "bg-accent-500/20 text-accent-200", bar: "bg-accent-500", text: "text-accent-200" },
  gerencia: { bubble: "bg-secondary-500/25 text-secondary-200", bar: "bg-secondary-500", text: "text-secondary-200" },
  donacion: { bubble: "bg-accent-500/15 text-accent-100", bar: "bg-accent-400", text: "text-accent-100" },
};

const MOVEMENT_LABEL: Record<FundMovement["kind"], string> = {
  transferencia: "Transferencia",
  prestamo_juank: "Préstamo de Juank",
  pago_prestamo: "Devolución a Juank",
};

const fundName = (funds: FundSummary[], key: FundKey | null): string =>
  key ? funds.find((f) => f.key === key)?.short ?? key : "—";

interface FundsPanelProps {
  funds: FundsSummary;
  movements: FundMovement[];
  onTransfer: (input: { fromFund: FundKey; toFund: FundKey; amount: number; note: string }) => void;
  onRemoveMovement: (id: string) => void;
}

// Los cuatro fondos en que se reparte el 25% de utilidad de cada venta.
const FundsPanel = ({ funds, movements, onTransfer, onRemoveMovement }: FundsPanelProps) => {
  const [open, setOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const sorted = [...movements].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-lg bg-primary-500/25 text-primary-100 flex items-center justify-center">
            <i className="ri-stack-line" />
          </span>
          <div>
            <h3 className="editable-title text-base font-bold">Fondos de la utilidad de venta</h3>
            <p className="text-[11px] text-foreground-500">
              La venta directa reparte el 25% completo entre los fondos. En ventas de asesor solo el
              margen que queda al admin (25% − su descuento) se acredita a Gerencia.
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="px-3.5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
        >
          <i className="ri-exchange-2-line" /> Transferir entre fondos
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {funds.funds.map((f) => {
          const tone = TONES[f.key];
          return (
            <div key={f.key} className="rounded-lg bg-background-200/30 border border-background-300/40 p-3.5 animate-fade-up">
              <div className="flex items-center justify-between gap-2">
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone.bubble}`}>
                  <i className={`${f.icon} text-lg`} />
                </span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-background-300/40 text-foreground-700">
                  {f.pct}%
                </span>
              </div>
              <p className="text-[11px] text-foreground-500 mt-3">{f.short}</p>
              <p className="text-lg font-heading font-extrabold text-foreground-950 break-words">
                {formatCOP(f.balance)}
              </p>
              <div className="h-1.5 rounded-full bg-background-300/40 overflow-hidden mt-2">
                <div
                  className={`h-full rounded-full ${tone.bar}`}
                  style={{
                    width: `${Math.min(100, Math.max(0, f.income > 0 ? (f.balance / f.income) * 100 : 0))}%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-[10px] text-foreground-500 mt-1.5">
                <span>Generado {formatCOP(f.income)}</span>
                {f.spent > 0 && <span className={tone.text}>Usado {formatCOP(f.spent)}</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2.5">
          <p className="text-[10px] text-foreground-500">Valor de catálogo vendido</p>
          <p className="text-sm font-bold text-foreground-950">{formatCOP(funds.totalSalesCatalog)}</p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2.5">
          <p className="text-[10px] text-foreground-500">Total en fondos</p>
          <p className="text-sm font-bold text-accent-200">{formatCOP(funds.totalBalance)}</p>
        </div>
        <div className="rounded-md bg-secondary-500/10 border border-secondary-500/30 px-3 py-2.5">
          <p className="text-[10px] text-secondary-200">Por devolver a Juank.unlimited</p>
          <p className="text-sm font-bold text-secondary-100">{formatCOP(funds.juankBalance)}</p>
        </div>
      </div>

      {sorted.length > 0 && (
        <div className="mt-4 pt-4 border-t border-background-300/25">
          <p className="text-[11px] font-semibold text-foreground-600 mb-2 flex items-center gap-1.5">
            <i className="ri-history-line text-accent-300" /> Movimientos de fondos
          </p>
          <ul className="divide-y divide-background-300/20">
            {sorted.slice(0, 12).map((m) => (
              <li key={m.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground-900">
                    {MOVEMENT_LABEL[m.kind]} ·{" "}
                    {m.kind === "transferencia"
                      ? `${fundName(funds.funds, m.fromFund)} → ${fundName(funds.funds, m.toFund)}`
                      : fundName(funds.funds, m.toFund ?? m.fromFund)}
                  </p>
                  <p className="text-[10px] text-foreground-500 mt-0.5">
                    {m.note || "Sin motivo"} · {formatDate(m.date)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-bold text-foreground-950 whitespace-nowrap">
                    {formatCOP(m.amount)}
                  </span>
                  {confirmId === m.id ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          onRemoveMovement(m.id);
                          setConfirmId(null);
                        }}
                        className="px-2.5 py-1 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                      >
                        Sí
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        className="px-2 py-1 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmId(m.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                      aria-label="Eliminar movimiento"
                      title="Eliminar movimiento"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <TransferFundsModal
        open={open}
        funds={funds}
        onClose={() => setOpen(false)}
        onSubmit={(input) => {
          onTransfer(input);
          setOpen(false);
        }}
      />
    </div>
  );
};

export default FundsPanel;